import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, buildQueryString } from '../lib/api';
import type {
  StoryCardShape,
  StoryDetailShape,
  AuditLogEntry,
  AuditLogFilters,
  ConfidenceLabel,
} from '../types/story';

// ─── Query keys ──────────────────────────────────────────────────────────────
//
// Decoupled from the public story hooks (T2) on purpose: invalidating
// `['stories']` as a raw string-array is exactly equivalent to
// `storyQueryKeys.all`, but avoids a forward/circular dependency between T2
// and T3. The WS subscription hook (T17) handles cross-surface invalidation
// explicitly, so this module never has to know about T2's registry shape.
const ADMIN_STORIES_KEY = ['admin', 'stories'] as const;
const ADMIN_AUDIT_KEY = ['admin', 'audit'] as const;
const PUBLIC_STORIES_KEY = ['stories'] as const;

export const adminStoryQueryKeys = {
  all: ADMIN_STORIES_KEY,
  list: [...ADMIN_STORIES_KEY, 'list'] as const,
  detail: (id: string) => [...ADMIN_STORIES_KEY, 'detail', id] as const,
  auditAll: ADMIN_AUDIT_KEY,
  audit: (filters: AuditLogFilters) => [...ADMIN_AUDIT_KEY, filters] as const,
  confidenceShadowSummary: [...ADMIN_STORIES_KEY, 'confidence-shadow', 'summary'] as const,
  confidenceShadow: (id: string) => [...ADMIN_STORIES_KEY, 'confidence-shadow', 'detail', id] as const,
};

// ─── Queries ─────────────────────────────────────────────────────────────────

export interface AdminStoriesResponse {
  stories: StoryCardShape[];
}

export interface AdminAuditResponse {
  entries: AuditLogEntry[];
  nextCursor: string | null;
}

/** List all admin-visible stories (includes archived). */
export function useAdminStories() {
  return useQuery<AdminStoriesResponse>({
    queryKey: adminStoryQueryKeys.list,
    queryFn: () => api.get<AdminStoriesResponse>('/admin/stories'),
    staleTime: 30_000,
  });
}

/** Fetch a single story with admin-only detail fields (canonicalArticleId, overrides, etc). */
export function useAdminStory(id: string) {
  return useQuery<StoryDetailShape>({
    queryKey: adminStoryQueryKeys.detail(id),
    queryFn: () => api.get<StoryDetailShape>(`/admin/stories/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

/** Fetch paginated admin audit log with optional filters (storyId, actorId, action, date range). */
export function useAdminAudit(filters: AuditLogFilters = {}) {
  const qs = buildQueryString(filters as Record<string, string | number | undefined>);
  const path = `/admin/audit${qs}`;
  return useQuery<AdminAuditResponse>({
    queryKey: adminStoryQueryKeys.audit(filters),
    queryFn: () => api.get<AdminAuditResponse>(path),
    staleTime: 10_000,
  });
}

// ─── Mutation invalidation helper ────────────────────────────────────────────

function useInvalidateOnSuccess() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ADMIN_STORIES_KEY });
    qc.invalidateQueries({ queryKey: ADMIN_AUDIT_KEY });
    qc.invalidateQueries({ queryKey: PUBLIC_STORIES_KEY });
  };
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export interface EjectArticleVars {
  storyId: string;
  articleId: string;
  reason?: string;
}

export interface EjectArticleResponse {
  sourceStoryId: string;
  destinationStoryId: string;
}

/** Eject an article from a story into a new standalone story. */
export function useEjectArticle() {
  const invalidate = useInvalidateOnSuccess();
  return useMutation<EjectArticleResponse, Error, EjectArticleVars>({
    mutationFn: (vars) =>
      api.post<EjectArticleResponse>(`/admin/stories/${vars.storyId}/eject`, {
        articleId: vars.articleId,
        reason: vars.reason,
      }),
    onSuccess: invalidate,
  });
}

export interface MergeStoriesVars {
  sourceStoryId: string;
  destinationStoryId: string;
  sectionChoice?: string;
  reason?: string;
}

export interface MergeStoriesResponse {
  destinationStoryId: string;
  absorbedStoryId: string;
}

/** Merge a source story into a destination story. */
export function useMergeStories() {
  const invalidate = useInvalidateOnSuccess();
  return useMutation<MergeStoriesResponse, Error, MergeStoriesVars>({
    mutationFn: (vars) =>
      api.post<MergeStoriesResponse>('/admin/stories/merge', vars),
    onSuccess: invalidate,
  });
}

export interface SplitStoryVars {
  storyId: string;
  articleIds: string[];
  reason?: string;
}

export interface SplitStoryResponse {
  sourceStoryId: string;
  newStoryId: string;
  movedArticleIds: string[];
}

/** Split a story by moving a subset of articles into a new story. */
export function useSplitStory() {
  const invalidate = useInvalidateOnSuccess();
  return useMutation<SplitStoryResponse, Error, SplitStoryVars>({
    mutationFn: (vars) =>
      api.post<SplitStoryResponse>(`/admin/stories/${vars.storyId}/split`, {
        articleIds: vars.articleIds,
        reason: vars.reason,
      }),
    onSuccess: invalidate,
  });
}

export interface OverrideCanonicalVars {
  storyId: string;
  articleId: string;
  reason?: string;
}

export interface OverrideCanonicalResponse {
  ok: true;
  canonicalArticleId: string;
}

/** Override the canonical (representative) article of a story. */
export function useOverrideCanonical() {
  const invalidate = useInvalidateOnSuccess();
  return useMutation<OverrideCanonicalResponse, Error, OverrideCanonicalVars>({
    mutationFn: (vars) =>
      api.post<OverrideCanonicalResponse>(`/admin/stories/${vars.storyId}/canonical`, {
        articleId: vars.articleId,
        reason: vars.reason,
      }),
    onSuccess: invalidate,
  });
}

export interface OverrideConfidenceVars {
  storyId: string;
  label: ConfidenceLabel;
  reason?: string;
}

export interface OverrideConfidenceResponse {
  ok: true;
  confidenceLabel: ConfidenceLabel;
}

/** Override the confidence label of a story. */
export function useOverrideConfidence() {
  const invalidate = useInvalidateOnSuccess();
  return useMutation<OverrideConfidenceResponse, Error, OverrideConfidenceVars>({
    mutationFn: (vars) =>
      api.post<OverrideConfidenceResponse>(`/admin/stories/${vars.storyId}/confidence`, {
        label: vars.label,
        reason: vars.reason,
      }),
    onSuccess: invalidate,
  });
}

export type ResetField = 'canonical' | 'title' | 'confidence';

export interface ResetOverridesVars {
  storyId: string;
  which: ResetField[];
  reason?: string;
}

export interface ResetOverridesResponse {
  ok: true;
  cleared: ResetField[];
}

/** Clear one or more admin overrides from a story. */
export function useResetOverrides() {
  const invalidate = useInvalidateOnSuccess();
  return useMutation<ResetOverridesResponse, Error, ResetOverridesVars>({
    mutationFn: (vars) =>
      api.post<ResetOverridesResponse>(`/admin/stories/${vars.storyId}/reset`, {
        which: vars.which,
        reason: vars.reason,
      }),
    onSuccess: invalidate,
  });
}

export interface TriggerSynthesisResponse {
  ok: true;
  queued: true;
}

/** Trigger an immediate synthesis run for a story (bypasses the 15-min debounce). */
export function useTriggerSynthesis() {
  const invalidate = useInvalidateOnSuccess();
  return useMutation<TriggerSynthesisResponse, Error, string>({
    mutationFn: (storyId) =>
      api.post<TriggerSynthesisResponse>(`/admin/stories/${storyId}/synthesise`, {}),
    onSuccess: invalidate,
  });
}

export interface ResolveTiersResponse {
  ok: true;
}

/** Re-resolve source tiers for every member of a story and recompute its confidence. */
export function useResolveTiers() {
  const invalidate = useInvalidateOnSuccess();
  return useMutation<ResolveTiersResponse, Error, string>({
    mutationFn: (storyId) =>
      api.post<ResolveTiersResponse>(`/admin/stories/${storyId}/resolve-tiers`, {}),
    onSuccess: invalidate,
  });
}

// ─── KAN-835: shadow evidence-origin confidence (admin-only, read-only) ─────
//
// The shadow never reaches readers. These hooks read the admin diff view only;
// nothing here mutates a story.

export interface ConfidenceShadowSummary {
  stories: number;
  differing: number;
  labelChanged: number;
  canonicalChanged: number;
  members: number;
  ineligibleMembers: number;
  policyVersions: string[];
  transitions: Array<{ from: ConfidenceLabel; to: ConfidenceLabel; count: number }>;
  readerEffect: 'none';
}

export interface ConfidenceShadowMember {
  articleId: string;
  role: string;
  origin: string | null;
  originTier: string | null;
  effectiveTier: string;
  independenceEligible: boolean;
  reasonCodes: string[];
  policyVersion: string;
}

export interface ConfidenceShadowDetail {
  storyId: string;
  sourceVersion: string;
  policyVersion: string;
  policyGeneration: number;
  inputFingerprint: string;
  currentLabel: ConfidenceLabel;
  shadowLabel: ConfidenceLabel;
  currentCanonicalArticleId: string;
  shadowCanonicalArticleId: string;
  canonicalChanged: boolean;
  orgCount: number;
  eligibleOriginCount: number;
  memberCount: number;
  ineligibleMemberCount: number;
  reasonCodes: string[];
  members: ConfidenceShadowMember[];
  computedAt: string;
}

/** Aggregate shadow-vs-current comparison across every story with a stored shadow. */
export function useConfidenceShadowSummary() {
  return useQuery<ConfidenceShadowSummary>({
    queryKey: adminStoryQueryKeys.confidenceShadowSummary,
    queryFn: () => api.get<ConfidenceShadowSummary>('/admin/stories/confidence-shadow/summary'),
    staleTime: 60_000,
  });
}

/**
 * The stored shadow for one story, or `null` when none has been computed
 * (the flag is off, or the story has not been recomputed since it was turned on).
 */
export function useConfidenceShadow(id: string) {
  return useQuery<ConfidenceShadowDetail | null>({
    queryKey: adminStoryQueryKeys.confidenceShadow(id),
    queryFn: async () => {
      try {
        return await api.get<ConfidenceShadowDetail>(`/admin/stories/${id}/confidence-shadow`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}
